import { apiAdminService } from "core/services/adminService";
import { apiService } from "core/services/apiService";
import { DutyItem } from "types";

const adminToken = "61d33daecce1eea6479be20e580be109b";

// Utility function to get workplace ID from localStorage
const getWorkplaceId = (): number => {
    const workplaceId = localStorage.getItem("workplace-id");
    return workplaceId ? Number(workplaceId) : 1; // Default to 1 if not found
};

// ChecklistPayload.ts
export interface ChecklistPayload {
    event: string;
    organization: {
        id: number;
    };
    workplace: {
        id: number;
    };
    executor_role: {
        id: number;
    };
    type: {
        id: number;
    };
    supervision_role: {
        id: number;
    };
    frequency: {
        id: number;
    };
    frequency_window_start: string; // ISO date string
    frequency_window_end: string;   // ISO date string
}



// Create Checklist API
export const createChecklist = async (payload: any): Promise<any> => {
    try {
        const workplaceId = getWorkplaceId();
        const currentUserId = localStorage.getItem("userId") || "1";
        const payloadWithWorkplace = {
            ...payload,
            workplace: `/api/workplaces/${workplaceId}`,
            author: `/api/users/${currentUserId}` // Add dynamic user ID for author
        };
        const { ...data } = apiService.responseHandler(
            await apiService.post(`/checklist_categories`, payloadWithWorkplace)
        );
        return data;
    } catch (error) {
        console.error('Error in createChecklist:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};



// Get All Checklists API
export const getAllChecklistCategories = async (): Promise<any[]> => {
    try {
        const workplaceId = getWorkplaceId();
        const { ...data } = apiService.responseHandler(
            await apiService.get(`/checklist_categories?page=1&workplace=${workplaceId}`)
        );
        
        // Handle the new response format with member array
        if (data && typeof data === 'object' && data.member && Array.isArray(data.member)) {
            return data.member;
        }
        
        // Handle the new response format with hydra:member array
        if (data && typeof data === 'object' && data['hydra:member'] && Array.isArray(data['hydra:member'])) {
            return data['hydra:member'];
        }
        
        // Handle nested response structures like { data: [...] }
        if (data && typeof data === 'object' && data.data && Array.isArray(data.data)) {
            return data.data;
        }
        
        // Handle direct array response
        if (Array.isArray(data)) {
            return data;
        }
        
        // Handle object with numeric keys
        if (data && typeof data === 'object') {
            return Object.values(data);
        }
        
        return [];
    } catch (error) {
        console.error('Error in getAllChecklistCategories:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};



// update Checklist by ID API
export const updateChecklist = async (
    id: number,
    updateName: any
): Promise<any> => {
    try {
        const payload = {
            label: updateName
        };
        const { ...data } = apiService.responseHandler(
            await apiService.put(`/checklist_categories/${id}`, payload)
        );
        return data;
    } catch (error) {
        console.error('Error in updateChecklist:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};



// Delete Checklist by ID API
export const deleteChecklist = async (id: number): Promise<any> => {
    try {
        const { ...data } = apiService.responseHandler(
            await apiService.delete(`/checklist_categories/${id}`)
        );
        return data;
    } catch (error) {
        console.error('Error in deleteChecklist:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};



// Get All Frequency

export const GetFrequencyList = async (): Promise<any> => {
    try {
        const workplaceId = getWorkplaceId();
        const { ...data } = apiService.responseHandler(
            await apiService.get(`/checklist_frequencies?page=1&workplace=${workplaceId}`)
        );
        
        // Handle the new response format with member array
        if (data && typeof data === 'object' && data.member && Array.isArray(data.member)) {
            return data.member;
        }
        
        // Handle the new response format with hydra:member array
        if (data && typeof data === 'object' && data['hydra:member'] && Array.isArray(data['hydra:member'])) {
            return data['hydra:member'];
        }
        
        // Handle nested response structures like { data: [...] }
        if (data && typeof data === 'object' && data.data && Array.isArray(data.data)) {
            return data.data;
        }
        
        // Handle direct array response
        if (Array.isArray(data)) {
            return data;
        }
        
        // Handle object with numeric keys
        if (data && typeof data === 'object') {
            return Object.values(data);
        }
        
        return [];
    } catch (error) {
        console.error('Error in GetFrequencyList:', error);
        throw error; // Re-throw to be handled by the calling function
    }
}


// Create Checklist Teamplete

export const createChecklistTemplete = async (payload): Promise<any> => {
    try {
        console.log('Creating checklist template with payload:', payload);
        const workplaceId = getWorkplaceId();
        const currentUserId = localStorage.getItem("userId") || "1";
        // Preserve incoming workplace/organization/type if provided by caller.
        const payloadWithWorkplace = {
            ...payload,
            workplace: payload?.workplace ?? `/api/workplaces/${workplaceId}`,
            organization: payload?.organization,
            type: payload?.type, // dynamic string 'workplace' | 'organization'
            author: `/api/users/${currentUserId}` // Add dynamic user ID for author
        };
        const { ...data } = apiService.responseHandler(
            await apiService.post(`/checklist_templates`, payloadWithWorkplace) // Pass the payload to the POST request
        );
        return data;
    } catch (error) {
        console.error('Error in createChecklistTemplete:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};



//create checklist task
export const createChecklistTask = async (payload: {
    parent: { id: number };
    section: string;
    duty: string;
    instructions: string;
    answer_type: string;
    expected_answer: string;
}): Promise<any> => {
    try {
        const workplaceId = getWorkplaceId();
        const payloadWithWorkplace = {
            ...payload,
            workplace: `/api/workplaces/${workplaceId}`
        };
        const { ...data } = apiService.responseHandler(
            await apiService.post(`/checklist-tasks`, payloadWithWorkplace)
        );
        return data;
    } catch (error) {
        console.error('Error in createChecklistTask:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

export const createChecklistUser = async (payload: {
    id: number,
    workplace: {
        id: number,
        name: string
    },
    label: string,
    category: {
        id: number,
        label: string
    },
    frequency: {
        id: number,
        label: string
    },
    template_owner: string,
    signoff_required: boolean
}
): Promise<any> => {
    try {
        const workplaceId = getWorkplaceId();
        const payloadWithWorkplace = {
            ...payload,
            workplace: `/api/workplaces/${workplaceId}`
        };
        const { ...data } = apiService.responseHandler(
            await apiService.post(`/create-checklist-user`, payloadWithWorkplace)
        );
        return data;
    } catch (error) {
        console.error('Error in createChecklistUser:', error);
        throw error; // Re-throw to be handled by the calling function
    }
}

export const dutyItemsTemplate = async (payload: DutyItem[]): Promise<any> => {
    try {
        const workplaceId = getWorkplaceId();
        const payloadWithWorkplace = {
            ...payload,
            workplace: `/api/workplaces/${workplaceId}`
        };
        const { ...data } = apiService.responseHandler(
            await apiService.post(`/create-duty`, payloadWithWorkplace)
        )
        return data;
    } catch (error) {
        console.error('Error in dutyItemsTemplate:', error);
        throw error; // Re-throw to be handled by the calling function
    }
}

export const getRolesAssignedToChecklist = async (): Promise<any> => {
    try {
        // Source security roles from localStorage (BYOD-security-roles)
        const stored = localStorage.getItem('BYOD-security-roles');
        const roles = stored ? JSON.parse(stored) : [];

        if (!Array.isArray(roles)) return [];

        // roles are SecurityRoleResponse[]; prefer numeric identifier, fallback to parsing @id
        const parseIdFromIri = (iri?: string): number | null => {
            if (!iri || typeof iri !== 'string') return null;
            const parts = iri.split('/')
                .filter(Boolean);
            const maybe = parts[parts.length - 1];
            const num = Number(maybe);
            return Number.isFinite(num) ? num : null;
        };

        // Get current workplace id (uses existing helper which defaults to 1 when missing)
        const currentWorkplaceId = getWorkplaceId();

        // Helper to extract workplace id from different representations (string IRI or object)
        const getRoleWorkplaceId = (wp: any): number | null => {
            if (!wp) return null;
            if (typeof wp === 'number') return wp;
            if (typeof wp === 'object') {
                if (typeof wp.id === 'number') return wp.id;
                if (typeof wp['@id'] === 'string') return parseIdFromIri(wp['@id']);
                return null;
            }
            if (typeof wp === 'string') return parseIdFromIri(wp);
            return null;
        };

        // Apply filtering rules:
        // - Exclude roles whose name contains exact substring 'BYOD'
        // - Include roles where (r as any).isSystemRole is truthy (system roles dominate workplace check)
        // - Include roles with no workplace (organization-level)
        // - Otherwise include roles whose workplace id matches currentWorkplaceId
        const filtered = roles.filter((r: any) => {
            try {
                if (r?.name && String(r.name).includes('BYOD')) return false;
                if ((r as any).isSystemRole) return true;
                const roleWpId = getRoleWorkplaceId(r.workplace);
                if (roleWpId === null) return true; // organization-level
                return roleWpId === currentWorkplaceId;
            } catch (e) {
                return false;
            }
        });

        return filtered.map((r: any) => ({
            id: (typeof r.identifier === 'number' ? r.identifier : parseIdFromIri(r['@id'])) ?? r.id ?? null,
            label: r.name || r.label || '',
            backgroundColor: r.backgroundColor || r.bgColor || '',
            textColor: r.textColor || r.fgColor || ''
        })).filter((r: any) => r.id !== null);
    } catch (error) {
        console.error('Error in getRolesAssignedToChecklist (localStorage):', error);
        return [];
    }
};

export const getUsers = async (): Promise<any[]> => {
    try {
        const workplaceId = getWorkplaceId();
        const { ...data } = apiService.responseHandler(
            await apiService.get(`/users?page=1&itemsPerPage=100&workplace=${workplaceId}`)
        );
        
        // Handle the new response format with member array
        if (data && typeof data === 'object' && data.member && Array.isArray(data.member)) {
            return data.member;
        }
        
        // Handle the new response format with hydra:member array
        if (data && typeof data === 'object' && data['hydra:member'] && Array.isArray(data['hydra:member'])) {
            return data['hydra:member'];
        }
        
        // Handle nested response structures like { data: [...] }
        if (data && typeof data === 'object' && data.data && Array.isArray(data.data)) {
            return data.data;
        }
        
        // Handle direct array response
        if (Array.isArray(data)) {
            return data;
        }
        
        // Handle object with numeric keys
        if (data && typeof data === 'object') {
            return Object.values(data);
        }
        
        return [];
    } catch (error) {
        console.error('Error in getUsers:', error);
        throw error; // Re-throw to be handled by the calling function
    }
}

// Get users scoped to a workplace via the workplace users endpoint
export const getUsersByWorkplace = async (workplace?: string | number): Promise<any[]> => {
    try {
        const workplaceId = workplace ?? getWorkplaceId();
        // Call the workplace users endpoint. Accept either numeric id or string identifier.
        const data = apiService.responseHandler(
            await apiService.get(`/workplaces/users`, { params: { workplace: workplaceId } })
        );
        // Normalize response formats (same approach as other helpers)
        let items: any[] = [];
        if (data && typeof data === 'object' && data.member && Array.isArray(data.member)) {
            items = data.member;
        } else if (data && typeof data === 'object' && data['hydra:member'] && Array.isArray(data['hydra:member'])) {
            items = data['hydra:member'];
        } else if (data && typeof data === 'object' && data.data && Array.isArray(data.data)) {
            items = data.data;
        } else if (Array.isArray(data)) {
            items = data;
        } else if (data && typeof data === 'object') {
            items = Object.values(data);
        } else {
            items = [];
        }

        // Map API user shape to expected User interface (handle snake_case keys and @id)
        const parseIdFromIri = (iri?: string): number | null => {
            if (!iri || typeof iri !== 'string') return null;
            const parts = iri.split('/').filter(Boolean);
            const maybe = parts[parts.length - 1];
            const num = Number(maybe);
            return Number.isFinite(num) ? num : null;
        };

        return items.map((item: any) => ({
            id: typeof item.id === 'number' ? item.id : parseIdFromIri(item['@id']) || null,
            '@id': item['@id'] ?? null,
            firstName: item.first_name ?? item.firstName ?? '',
            lastName: item.last_name ?? item.lastName ?? '',
            email: item.email ?? '',
            // Include additional fields if available for filtering
            securityRole: item.securityRole || item.security_role || null,
            userType: item.userType || item.user_type || null,
            separation: item.separation ?? false,
            // Keep original item for any additional fields
            _raw: item
        }));
    } catch (error) {
        console.error('Error in getUsersByWorkplace:', error);
        throw error;
    }
};


export const createChecklistGroup = async (payload): Promise<any> => {
    try {
    console.log('Creating checklist group with payload:', payload);
    const currentUserId = localStorage.getItem("userId") || "1";
        
        // Update the payload to use dynamic user ID for author
        const updatedPayload = {
            ...payload,
            author: `/api/users/${currentUserId}`
        };
        
        if(payload.isUpdate !== null && payload.isUpdate !== undefined){
            const { ...data } = apiService.responseHandler(
                await apiService.put(`/checklist_groups/${payload.isUpdate}`, updatedPayload) // Pass the updated payload to the PUT request
            );
            return data;
        }else{
            const { ...data } = apiService.responseHandler(
                await apiService.post(`/checklist_groups`, updatedPayload) // Pass the updated payload to the POST request
            );

            return data;
        }
    } catch (error) {
        console.error('Error in createChecklistGroup:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

export const deleteChecklistGroup = async (id): Promise<any> => {
    try {
        console.log('Deleting checklist group with ID:', id);
        const { ...data } = apiService.responseHandler(
            await apiService.delete(`/checklist_groups/${id}`)
        );

        return data;
    } catch (error) {
        console.error('Error in deleteChecklistGroup:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Get All Checklist Groups API
export const getAllChecklistGroups = async (): Promise<any[]> => {
    try {
        const workplaceId = getWorkplaceId();
        // Calls the checklist_groups endpoint and returns the result as an array
        const { ...data } = apiService.responseHandler(
            await apiService.get(`/checklist_groups?page=1&workplace=${workplaceId}`)
        );
        
        // Handle the new response format with member array
        if (data && typeof data === 'object' && data.member && Array.isArray(data.member)) {
            return data.member;
        }
        
        // Handle the new response format with hydra:member array
        if (data && typeof data === 'object' && data['hydra:member'] && Array.isArray(data['hydra:member'])) {
            return data['hydra:member'];
        }
        
        // Handle nested response structures like { data: [...] }
        if (data && typeof data === 'object' && data.data && Array.isArray(data.data)) {
            return data.data;
        }
        
        // Handle direct array response
        if (Array.isArray(data)) {
            return data;
        }
        
        // Handle object with numeric keys
        if (data && typeof data === 'object') {
            return Object.values(data);
        }
        
        return [];
    } catch (error) {
        console.error('Error in getAllChecklistGroups:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Get Checklist Templates API
export const getChecklistTemplates = async (page = 1): Promise<any[]> => {
    try {
        const workplaceId = getWorkplaceId();
        const { ...data } = apiService.responseHandler(
            await apiService.get(`/checklist_templates?page=${page}&workplace=${workplaceId}`)
        );
        
        // Handle the new response format with member array
        if (data && typeof data === 'object' && data.member && Array.isArray(data.member)) {
            return data.member;
        }
        
        // Handle the new response format with hydra:member array
        if (data && typeof data === 'object' && data['hydra:member'] && Array.isArray(data['hydra:member'])) {
            return data['hydra:member'];
        }
        
        // Handle nested response structures like { data: [...] }
        if (data && typeof data === 'object' && data.data && Array.isArray(data.data)) {
            return data.data;
        }
        
        // Handle direct array response
        if (Array.isArray(data)) {
            return data;
        }
        
        // Handle object with numeric keys
        if (data && typeof data === 'object') {
            return Object.values(data);
        }
        
        return [];
    } catch (error) {
        console.error('Error in getChecklistTemplates:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Get latest checklist template by templateUid
export const getLatestChecklistTemplateByUid = async (
    templateUid: string
): Promise<any> => {
    try {
        const { ...data } = apiService.responseHandler(
            await apiService.get(`/checklist_templates/${templateUid}/latest`)
        );
        return data;
    } catch (error) {
        console.error('Error in getLatestChecklistTemplateByUid:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Update Checklist Template by ID API
export const updateChecklistTemplate = async (id: number, payload: any): Promise<any> => {
    try {
        const { ...data } = apiService.responseHandler(
            await apiService.put(`/checklist_templates/${id}`, payload)
        );
        return data;
    } catch (error) {
        console.error('Error in updateChecklistTemplate:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Delete Checklist Template by templateUid API
export const deleteChecklistTemplate = async (templateUid: string): Promise<any> => {
    try {
        if (!templateUid || templateUid.trim() === '') {
            throw new Error('Template UID is required for deletion');
        }
        
        console.log('Deleting checklist template with UID:', templateUid);
        const { ...data } = apiService.responseHandler(
            await apiService.delete(`/checklist_templates/${templateUid}`)
        );
        return data;
    } catch (error) {
        console.error('Error in deleteChecklistTemplate:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Get Scheduled Checklist Templates API
export const getScheduledChecklistTemplates = async (page = 1, workplace?: number): Promise<any[]> => {
    try {
        const workplaceId = workplace || getWorkplaceId();
        const { ...data } = apiService.responseHandler(
            await apiService.get(`/checklist_scheduled_templates?page=${page}&workplace=${workplaceId}`)
        );
        
        // Handle the new response format with member array
        if (data && typeof data === 'object' && data.member && Array.isArray(data.member)) {
            return data.member;
        }
        
        // Handle the new response format with hydra:member array
        if (data && typeof data === 'object' && data['hydra:member'] && Array.isArray(data['hydra:member'])) {
            return data['hydra:member'];
        }
        
        // Handle nested response structures like { data: [...] }
        if (data && typeof data === 'object' && data.data && Array.isArray(data.data)) {
            return data.data;
        }
        
        // Handle direct array response
        if (Array.isArray(data)) {
            return data;
        }
        
        // Handle object with numeric keys
        if (data && typeof data === 'object') {
            return Object.values(data);
        }
        
        return [];
    } catch (error) {
        console.error('Error in getScheduledChecklistTemplates:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Get Categorized Checklist Schedule Events API
export const getCategorizedChecklistScheduleEvents = async (date: string, workplace?: number): Promise<any> => {
    try {
        const workplaceId = workplace || getWorkplaceId();
        const { ...data } = apiService.responseHandler(
            await apiService.get(`/checklist_schedule_event/categorized?date=${date}&workplace=${workplaceId}`)
        );
        return data;
    } catch (error) {
        console.error('Error in getCategorizedChecklistScheduleEvents:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Get Outstanding Checklist Schedule Events API
export const getChecklistOutstanding = async (date: string, workplace?: number): Promise<any> => {
    try {
        const workplaceId = workplace || getWorkplaceId();
        const { ...data } = apiService.responseHandler(
            await apiService.get(`/checklist_schedule_event/outstanding-business-day?pagination=false&date=${date}&workplace=${workplaceId}`)
        );
        return data;
    } catch (error) {
        console.error('Error in getChecklistOutstanding:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Get On Time Stats (WTD/PTD/YTD) for checklist main page
export const getOnTimeStats = async (): Promise<Array<{ period: string; onTime: number; signOff: number }>> => {
    try {
        const { ...data } = apiService.responseHandler(
            await apiService.get(`/on_time_stats`)
        );

        const items = Array.isArray((data as any)?.member)
            ? (data as any).member
            : Array.isArray((data as any)?.['hydra:member'])
                ? (data as any)['hydra:member']
                : Array.isArray(data)
                    ? data
                    : [];

        return items.map((it: any) => ({
            period: it.period,
            onTime: Number(it.onTime ?? 0),
            signOff: Number(it.signOff ?? 0),
        }));
    } catch (error) {
        console.error('Error in getOnTimeStats:', error);
        return [];
    }
};

// Approve checklist schedule event API
export const approveChecklistScheduleEvent = async (id: number): Promise<any> => {
    try {
        const { ...data } = apiService.responseHandler(
            await apiService.post(`/checklist_schedule_event/${id}/approve`)
        );
        return data;
    } catch (error) {
        console.error('Error in approveChecklistScheduleEvent:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Get checklist schedule by ID API
export const getChecklistScheduleById = async (checklistScheduleId: number): Promise<any> => {
    try {
        const { ...data } = apiService.responseHandler(
            await apiService.get(`/checklist_schedules/${checklistScheduleId}`)
        );
        return data;
    } catch (error) {
        console.error('Error in getChecklistScheduleById:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Deactivate checklist schedule API
export const deactivateChecklistSchedule = async (checklistScheduleId: number): Promise<any> => {
    try {
        const { ...data } = apiService.responseHandler(
            await apiService.delete(`/checklist_schedules/deactive/${checklistScheduleId}`)
        );
        return data;
    } catch (error) {
        console.error('Error in deactivateChecklistSchedule:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Get checklist template by ID API
export const getChecklistTemplateById = async (templateId: number): Promise<any> => {
    try {
        const { ...data } = apiService.responseHandler(
            await apiService.get(`/checklist_templates/${templateId}`)
        );
        return data;
    } catch (error) {
        console.error('Error in getChecklistTemplateById:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};

// Get checklist responses for an event API
export const getChecklistResponsesByEvent = async (eventId: number): Promise<any> => {
    try {
        const response = await apiService.get(
            `/checklist_responses/event/${eventId}?page=1&itemsPerPage=10&pagination=false`
        );
        return response.data;
    } catch (error) {
        console.error('Error in getChecklistResponsesByEvent:', error);
        throw error; // Re-throw to be handled by the calling function
    }
};